"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Box, Flex, Card, Grid, Heading, Text, Badge, Button, IconButton, Dialog, TextField, Select, ScrollArea, Separator } from "@radix-ui/themes"
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Package,
  Truck,
  User,
  MapPin,
  Phone,
  DollarSign,
  Calendar,
  Filter,
  Search,
  Eye,
  MoreVertical,
  ArrowUpDown,
  X,
  Printer
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface OrderItem {
  foodItem: {
    _id: string
    name: string
    price: number
    image?: string
  } | string // Can be string ID for dine-in orders
  quantity: number
  price: number
  specialInstructions?: string
  customizations?: string[]
}

interface Order {
  _id: string
  orderNumber: string
  restaurant: string
  customer?: {
    _id: string
    name: string
    email: string
    phone: string
  }
  customerInfo?: {
    name: string
    phone: string
  }
  items: OrderItem[]
  orderType: "delivery" | "pickup" | "dine-in"
  table?: {
    _id: string
    tableNumber: string
  }
  status: "placed" | "pending" | "paid" | "confirmed" | "preparing" | "ready" | "picked-up" | "on-the-way" | "delivered" | "cancelled" | "served"
  deliveryAddress?: {
    street: string
    city: string
    state: string
    zipCode: string
    instructions?: string
  }
  pricing: {
    subtotal: number
    deliveryFee: number
    tax: number
    discount: number
    total: number
  }
  specialInstructions?: string
  estimatedDeliveryTime?: string
  actualDeliveryTime?: string
  createdAt: string
  updatedAt: string
}

const RestaurantOrdersPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { t, dir } = useLanguage() // Get translation function and direction
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [statusFilter, setStatusFilter] = useState("all")
  const [orderTypeFilter, setOrderTypeFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [foodItemsMap, setFoodItemsMap] = useState<{ [key: string]: any }>({})
  const [restaurantInfo, setRestaurantInfo] = useState<any>(null)

  useEffect(() => {
    fetchOrders()
    fetchRestaurantInfo()
  }, [restaurantId])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      // Set a high limit to get all orders
      const response = await restaurantPortalAPI.getOrders(restaurantId!, { limit: 1000 })
      const ordersData = response.data.orders || []

      // Debug: Log the first order to see its structure
      if (ordersData.length > 0) {
        console.log('First order structure:', ordersData[0])
      }

      setOrders(ordersData)

      // Fetch food item details for dine-in orders
      await fetchFoodItemDetails(ordersData)
    } catch (error) {
      console.error("Error fetching orders:", error)
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const fetchRestaurantInfo = async () => {
    try {
      const response = await restaurantPortalAPI.getRestaurantInfo(restaurantId!)
      setRestaurantInfo(response.data.restaurant || {})
    } catch (error) {
      console.error("Error fetching restaurant info:", error)
    }
  }

  const fetchFoodItemDetails = async (ordersData: Order[]) => {
    const foodItemIds = new Set<string>()

    // Collect all food item IDs from dine-in orders
    ordersData.forEach(order => {
      order.items.forEach(item => {
        if (typeof item.foodItem === 'string') {
          foodItemIds.add(item.foodItem)
        }
      })
    })

    // Fetch food item details for each ID
    const foodItemsData: { [key: string]: any } = {}
    for (const itemId of Array.from(foodItemIds)) {
      try {
        const response = await restaurantPortalAPI.getFoodItem(itemId)
        foodItemsData[itemId] = response.data.data
      } catch (error) {
        console.error(`Error fetching food item ${itemId}:`, error)
        foodItemsData[itemId] = { name: "Unknown Item", price: 0 }
      }
    }

    setFoodItemsMap(foodItemsData)
  }

  const handleStatusUpdate = async (orderId: string, newStatus: string, note?: string) => {
    try {
      await restaurantPortalAPI.updateOrderStatus(orderId, {
        status: newStatus,
        note
      })
      setOrders(orders =>
        (orders || []).map(order =>
          order._id === orderId
            ? { ...order, status: newStatus as any }
            : order
        )
      )
    } catch (error) {
      console.error("Error updating order status:", error)
    }
  }

  const getStatusColor = (status: any) => {
    if (typeof status !== 'string') return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    switch (status) {
      case "pending":
      case "placed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
      case "confirmed":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
      case "preparing":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
      case "ready":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      case "picked-up":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
      case "on-the-way":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400"
      case "delivered":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      case "served":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    }
  }

  const getStatusIcon = (status: any) => {
    if (typeof status !== 'string') return <AlertCircle className="w-4 h-4" />
    switch (status) {
      case "pending":
      case "placed":
        return <Clock className="w-4 h-4" />
      case "confirmed":
        return <CheckCircle className="w-4 h-4" />
      case "preparing":
        return <Package className="w-4 h-4" />
      case "ready":
        return <CheckCircle className="w-4 h-4" />
      case "picked-up":
        return <Truck className="w-4 h-4" />
      case "on-the-way":
        return <Truck className="w-4 h-4" />
      case "delivered":
        return <CheckCircle className="w-4 h-4" />
      case "cancelled":
        return <XCircle className="w-4 h-4" />
      case "served":
        return <CheckCircle className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const getOrderTypeIcon = (orderType: any) => {
    if (typeof orderType !== 'string') return <Package className="w-4 h-4" />
    switch (orderType) {
      case "delivery":
        return <Truck className="w-4 h-4" />
      case "pickup":
        return <Package className="w-4 h-4" />
      case "dine-in":
        return <User className="w-4 h-4" />
      default:
        return <Package className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) return "SDG 0.00"
    return `SDG ${amount.toFixed(2)}`
  }

  const printOrder = (order: Order) => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const customerName = order.customer?.name || order.customerInfo?.name || "Guest"
    const customerPhone = order.customer?.phone || order.customerInfo?.phone || "N/A"
    const tableNumber = order.table?.tableNumber || "N/A"
    const restaurantName = restaurantInfo?.name || "Restaurant"

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order Receipt</title>
        <style>
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.2;
            margin: 0;
            padding: 10px;
            width: 80mm;
            max-width: 80mm;
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
            margin-bottom: 10px;
          }
          .restaurant-name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .order-info {
            margin-bottom: 10px;
          }
          .customer-info {
            margin-bottom: 10px;
          }
          .items {
            margin-bottom: 10px;
          }
          .item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          .item-name {
            flex: 1;
          }
          .item-qty {
            margin: 0 10px;
          }
          .item-price {
            text-align: right;
          }
          .total {
            border-top: 1px dashed #000;
            padding-top: 10px;
            margin-top: 10px;
            text-align: right;
            font-weight: bold;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
                 <div class="header">
           <div class="restaurant-name">${restaurantName.toUpperCase()}</div>
           <div>${t("orderReceipt")}</div>
         </div>
        
        <div class="order-info">
          <div>Order #: ${order.orderNumber}</div>
          <div>Date: ${formatDate(order.createdAt)}</div>
          <div>Type: ${order.orderType.toUpperCase()}</div>${order.table ? `<div>Table: ${tableNumber}</div>` : ''}
        </div>
        
        <div class="customer-info">
          <div>Customer: ${customerName}</div>
          <div>Phone: ${customerPhone}</div>
        </div>
        
                 <div class="items">${order.items.map(item => {
      const itemName = typeof item.foodItem === 'string'
        ? (foodItemsMap[item.foodItem]?.name || 'Unknown Item')
        : item.foodItem.name
      return `
               <div class="item">
                 <span class="item-name">${itemName}</span>
                 <span class="item-qty">x${item.quantity}</span>
                 <span class="item-price">$${(item.price * item.quantity).toFixed(2)}</span>
               </div>${item.specialInstructions ? `<div style="font-size: 10px; margin-left: 10px; color: #666;">Note: ${item.specialInstructions}</div>` : ''}
             `
    }).join('')}
         </div>
        
        <div class="total">
          <div>Subtotal: SDG ${order.pricing.subtotal.toFixed(2)}</div>${order.pricing.tax > 0 ? `<div>Tax: SDG ${order.pricing.tax.toFixed(2)}</div>` : ''}
          ${order.pricing.deliveryFee > 0 ? `<div>Delivery: $${order.pricing.deliveryFee.toFixed(2)}</div>` : ''}
          ${order.pricing.discount > 0 ? `<div>Discount: -$${order.pricing.discount.toFixed(2)}</div>` : ''}
          <div style="font-size: 14px;">TOTAL: $${order.pricing.total.toFixed(2)}</div>
        </div>
        
        <div class="footer">
          <div>Thank you for your order!</div>
          <div>Status: ${order.status.toUpperCase()}</div>
          <div>${new Date().toLocaleString()}</div>
        </div>
      </body>
      </html>
    `

    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }

  const filteredAndSortedOrders = (orders || [])
    .filter(order => {
      // Safety check: ensure order has required properties
      if (!order || typeof order.orderNumber !== 'string' || typeof order.status !== 'string' || typeof order.orderType !== 'string') {
        return false
      }

      const customerName = order.customer?.name || order.customerInfo?.name || ""
      const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customerName.toLowerCase().includes(searchTerm.toLowerCase())

      const effectiveStatus = order.status === "pending" ? "placed" : order.status
      const matchesStatus = statusFilter === "all" || effectiveStatus === statusFilter
      const matchesOrderType = orderTypeFilter === "all" || order.orderType === orderTypeFilter

      return matchesSearch && matchesStatus && matchesOrderType
    })
    .sort((a, b) => {
      let aValue: any = a[sortBy as keyof Order]
      let bValue: any = b[sortBy as keyof Order]

      if (sortBy === "createdAt" || sortBy === "updatedAt") {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingOrders")} />
  }

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header */}
      <PageHeader
        title={t("orders")}
        subtitle={t("manageAndTrackRestaurantOrders")}
      />

      {/* Stats */}
      <Grid columns={{ initial: "1", md: "4" }} gap="6" mb="6">
        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{
              padding: "16px",
              borderRadius: "var(--radius-4)",
              backgroundColor: "var(--blue-4)"
            }}>
              <Package size={28} style={{ color: "var(--red-9)" }} />
            </Box>
            <Flex direction="column" justify="center" gap="1">
              <Text size="2" weight="medium" color="gray">{t("totalOrders")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: 1 }}>{(orders || []).length}</Text>
            </Flex>
          </Flex>
        </Card>

        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{
              padding: "16px",
              borderRadius: "var(--radius-4)",
              backgroundColor: "var(--yellow-4)"
            }}>
              <Clock size={28} style={{ color: "var(--yellow-9)" }} />
            </Box>
            <Flex direction="column" justify="center" gap="1">
              <Text size="2" weight="medium" color="gray">{t("pendingOrders")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: 1 }}>
                {(orders || []).filter(o => ["placed", "pending", "confirmed", "preparing"].includes(o.status)).length}
              </Text>
            </Flex>
          </Flex>
        </Card>

        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{
              padding: "16px",
              borderRadius: "var(--radius-4)",
              backgroundColor: "var(--green-4)"
            }}>
              <CheckCircle size={28} style={{ color: "var(--green-9)" }} />
            </Box>
            <Flex direction="column" justify="center" gap="1">
              <Text size="2" weight="medium" color="gray">{t("completed")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: 1 }}>
                {(orders || []).filter(o => ["delivered", "served"].includes(o.status)).length}
              </Text>
            </Flex>
          </Flex>
        </Card>

        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{
              padding: "16px",
              borderRadius: "var(--radius-4)",
              backgroundColor: "var(--purple-4)"
            }}>
              <DollarSign size={28} style={{ color: "var(--purple-9)" }} />
            </Box>
            <Flex direction="column" justify="center" gap="1">
              <Text size="2" weight="medium" color="gray">{t("totalRevenue")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: 1 }}>
                {formatCurrency((orders || []).reduce((sum, order) => sum + (order.pricing?.total || (order as any).total || 0), 0))}
              </Text>
            </Flex>
          </Flex>
        </Card>
      </Grid>

      {/* Filters */}
      <Card>
        <Grid columns={{ initial: "1", md: "5" }} gap="4" align="center">
          <TextField.Root
            placeholder={t("searchOrders")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          >
            <TextField.Slot>
              <Search size={16} />
            </TextField.Slot>
          </TextField.Root>

          <Select.Root value={statusFilter} onValueChange={setStatusFilter}>
            <Select.Trigger style={{ width: "100%" }} placeholder={t("allStatus")} />
            <Select.Content>
              <Select.Item value="all">{t("allStatus")}</Select.Item>
              <Select.Item value="placed">{t("placed")}</Select.Item>
              <Select.Item value="confirmed">{t("confirmed")}</Select.Item>
              <Select.Item value="preparing">{t("preparing")}</Select.Item>
              <Select.Item value="ready">{t("ready")}</Select.Item>
              <Select.Item value="picked-up">{t("pickedUp")}</Select.Item>
              <Select.Item value="on-the-way">{t("onTheWay")}</Select.Item>
              <Select.Item value="delivered">{t("delivered")}</Select.Item>
              <Select.Item value="cancelled">{t("cancelled")}</Select.Item>
              <Select.Item value="served">{t("served")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Select.Root value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
            <Select.Trigger style={{ width: "100%" }} placeholder={t("allTypes")} />
            <Select.Content>
              <Select.Item value="all">{t("allTypes")}</Select.Item>
              <Select.Item value="delivery">{t("delivery")}</Select.Item>
              <Select.Item value="pickup">{t("pickup")}</Select.Item>
              <Select.Item value="dine-in">{t("dineIn")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Select.Root
            value={`${sortBy}-${sortOrder}`}
            onValueChange={(value) => {
              const [field, order] = value.split("-")
              setSortBy(field)
              setSortOrder(order as "asc" | "desc")
            }}
          >
            <Select.Trigger style={{ width: "100%" }} />
            <Select.Content>
              <Select.Item value="createdAt-desc">{t("newestFirst")}</Select.Item>
              <Select.Item value="createdAt-asc">{t("oldestFirst")}</Select.Item>
              <Select.Item value="pricing.total-desc">{t("highestTotal")}</Select.Item>
              <Select.Item value="pricing.total-asc">{t("lowestTotal")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Text size="2" color="gray">
            {t("ofOrders", { count: filteredAndSortedOrders.length.toString(), total: orders.length.toString() })}
          </Text>
        </Grid>
      </Card>

      {/* Orders List */}
      <Flex direction="column" gap="4">
        {filteredAndSortedOrders.map((order) => (
          <Card key={order._id}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("order")} #{typeof order.orderNumber === 'string' ? order.orderNumber : t("notAvailable")}
                  </h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)} dark:text-white`}>
                    {getStatusIcon(order.status)}
                    <span className="ml-1 capitalize">{typeof order.status === 'string' ? t(order.status.replace("-", "")) : t("unknown")}</span>
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-white">
                    {getOrderTypeIcon(order.orderType)}
                    <span className="ml-1 capitalize">{typeof order.orderType === 'string' ? t(order.orderType.replace("-", "")) : t("unknown")}</span>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{order.customer?.name || order.customerInfo?.name || t("notAvailable")}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{order.customer?.phone || order.customerInfo?.phone || t("notAvailable")}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{formatDate(order.createdAt)}</span>
                  </div>
                </div>

                {order.deliveryAddress && (
                  <div className="flex items-center space-x-2 mt-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {order.deliveryAddress.street}, {order.deliveryAddress.city}
                    </span>
                  </div>
                )}

                {order.table && (
                  <div className="flex items-center space-x-2 mt-2 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{t("table")} {order.table.tableNumber}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(order.pricing.total)}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {order.items.length} {t("items")}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedOrder(order)
                    setShowOrderDetails(true)
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title={t("viewDetails")}
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex space-x-2">
                {typeof order.status === 'string' && order.status === "placed" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "confirmed")}
                    className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Confirm
                  </button>
                )}
                {typeof order.status === 'string' && order.status === "confirmed" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "preparing")}
                    className="px-3 py-1 text-xs bg-orange-100 text-orange-800 rounded hover:bg-orange-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Start Preparing
                  </button>
                )}
                {typeof order.status === 'string' && order.status === "preparing" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "ready")}
                    className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Mark Ready
                  </button>
                )}
                {typeof order.status === 'string' && order.status === "ready" && typeof order.orderType === 'string' && order.orderType === "delivery" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "on-the-way")}
                    className="px-3 py-1 text-xs bg-indigo-100 text-indigo-800 rounded hover:bg-indigo-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Out for Delivery
                  </button>
                )}
                {typeof order.status === 'string' && order.status === "ready" && typeof order.orderType === 'string' && order.orderType === "pickup" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "picked-up")}
                    className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded hover:bg-purple-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Picked Up
                  </button>
                )}
                {typeof order.status === 'string' && order.status === "ready" && typeof order.orderType === 'string' && order.orderType === "dine-in" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "served")}
                    className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Served
                  </button>
                )}
                {typeof order.status === 'string' && order.status === "on-the-way" && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "delivered")}
                    className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Delivered
                  </button>
                )}
                {typeof order.status === 'string' && ["placed", "confirmed", "preparing"].includes(order.status) && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, "cancelled")}
                    className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors dark:bg-gray-700 dark:text-white"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </Flex>

      {filteredAndSortedOrders.length === 0 && !loading && (
        <Card>
          <Flex direction="column" align="center" justify="center" p="9" gap="3">
            <Package size={48} style={{ color: "var(--gray-9)" }} />
            <Heading size="4">{t("noOrdersFound")}</Heading>
            <Text size="2" color="gray">
              {searchTerm || statusFilter !== "all" || orderTypeFilter !== "all"
                ? t("tryAdjustingFilters")
                : t("ordersWillAppearHere")}
            </Text>
          </Flex>
        </Card>
      )}

      {/* Order Details Modal */}
      <Dialog.Root open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <Dialog.Content style={{ maxWidth: 720, maxHeight: "90vh" }}>
          <ScrollArea>
            <Flex direction="column" gap="6">
              <Flex align="center" justify="between">
                <Dialog.Title>
                  {t("orderDetails")} #{selectedOrder?.orderNumber}
                </Dialog.Title>
                <Flex gap="2">
                  <IconButton
                    variant="soft"
                    color="blue"
                    onClick={() => selectedOrder && printOrder(selectedOrder)}
                    title={t("printReceipt")}
                  >
                    <Printer size={18} />
                  </IconButton>
                </Flex>
              </Flex>

              {selectedOrder && (

                <div className="space-y-6">
                  {/* Customer Info */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t("customerInformation")}</h3>
                    <div className="bg-gray-50 p-4 rounded-lg dark:bg-gray-800">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t("name")}:</span>
                          <p className="text-gray-900 dark:text-gray-100">{selectedOrder.customer?.name || selectedOrder.customerInfo?.name || t("notAvailable")}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t("phone")}:</span>
                          <p className="text-gray-900 dark:text-gray-100">{selectedOrder.customer?.phone || selectedOrder.customerInfo?.phone || t("notAvailable")}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t("email")}:</span>
                          <p className="text-gray-900 dark:text-gray-100">{selectedOrder.customer?.email || t("notAvailable")}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t("orderType")}:</span>
                          <p className="text-gray-900 dark:text-gray-100 capitalize">{t(selectedOrder.orderType.replace("-", ""))}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Address */}
                  {selectedOrder.deliveryAddress && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t("deliveryAddress")}</h3>
                      <div className="bg-gray-50 p-4 rounded-lg dark:bg-gray-700">
                        <p className="text-gray-900 dark:text-white">
                          {selectedOrder.deliveryAddress.street}<br />
                          {selectedOrder.deliveryAddress.city}, {selectedOrder.deliveryAddress.state} {selectedOrder.deliveryAddress.zipCode}
                        </p>
                        {selectedOrder.deliveryAddress.instructions && (
                          <p className="text-gray-600 dark:text-white mt-2">
                            <span className="font-medium">{t("instructions")}:</span> {selectedOrder.deliveryAddress.instructions}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Table Info */}
                  {selectedOrder.table && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t("tableInformation")}</h3>
                      <div className="bg-gray-50 p-4 rounded-lg dark:bg-gray-700">
                        <p className="text-gray-900 dark:text-white">{t("table")} {selectedOrder.table.tableNumber}</p>
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t("orderItems")}</h3>
                    <div className="space-y-3">
                      {selectedOrder.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-700">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              {typeof item.foodItem === 'string' ? (
                                <span className="text-gray-600 dark:text-white">{t("dineInOrder")}</span>
                              ) : (
                                item.foodItem.image && (
                                  <img
                                    src={item.foodItem.image}
                                    alt={item.foodItem.name}
                                    className="w-12 h-12 object-cover rounded"
                                  />
                                )
                              )}
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {typeof item.foodItem === 'string'
                                    ? (foodItemsMap[item.foodItem]?.name || t("unknownItem"))
                                    : item.foodItem.name
                                  }
                                </p>
                                <p className="text-sm text-gray-600 dark:text-white">{t("qty")}: {item.quantity}</p>
                                {item.specialInstructions && (
                                  <p className="text-sm text-gray-600 dark:text-white">
                                    {t("note")}: {item.specialInstructions}
                                  </p>
                                )}
                                {item.customizations && item.customizations.length > 0 && (
                                  <p className="text-sm text-gray-600 dark:text-white">
                                    {t("customizations")}: {item.customizations.join(", ")}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formatCurrency(item.price * item.quantity)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t("pricing")}</h3>
                    <div className="bg-gray-50 p-4 rounded-lg dark:bg-gray-700">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-white">{t("subtotal")}:</span>
                          <span className="text-gray-900">{formatCurrency(selectedOrder.pricing.subtotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-white">{t("deliveryFee")}:</span>
                          <span className="text-gray-900">{formatCurrency(selectedOrder.pricing.deliveryFee)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-white">{t("tax")}:</span>
                          <span className="text-gray-900">{formatCurrency(selectedOrder.pricing.tax)}</span>
                        </div>
                        {selectedOrder.pricing.discount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-white">{t("discount")}:</span>
                            <span className="text-gray-900">-{formatCurrency(selectedOrder.pricing.discount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-gray-200 pt-2">
                          <span className="font-medium text-gray-900 dark:text-white">{t("total")}:</span>
                          <span className="font-bold text-gray-900">{formatCurrency(selectedOrder.pricing.total)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Special Instructions */}
                  {selectedOrder.specialInstructions && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{t("specialInstructions")}</h3>
                      <div className="bg-gray-50 p-4 rounded-lg dark:bg-gray-700">
                        <p className="text-gray-900 dark:text-white">{selectedOrder.specialInstructions}</p>
                      </div>
                    </div>
                  )}

                  {/* Status Update */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Update Status</h3>
                    <div className="flex flex-wrap gap-2">
                      {["placed", "pending"].includes(selectedOrder.status) && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "confirmed")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("confirmOrder")}
                        </button>
                      )}
                      {selectedOrder.status === "confirmed" && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "preparing")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("startPreparing")}
                        </button>
                      )}
                      {selectedOrder.status === "preparing" && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "ready")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("markReady")}
                        </button>
                      )}
                      {selectedOrder.status === "ready" && selectedOrder.orderType === "delivery" && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "on-the-way")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-indigo-100 text-indigo-800 rounded-lg hover:bg-indigo-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("outForDelivery")}
                        </button>
                      )}
                      {selectedOrder.status === "ready" && selectedOrder.orderType === "pickup" && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "picked-up")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("pickedUp")}
                        </button>
                      )}
                      {selectedOrder.status === "ready" && selectedOrder.orderType === "dine-in" && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "served")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("served")}
                        </button>
                      )}
                      {selectedOrder.status === "on-the-way" && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "delivered")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("delivered")}
                        </button>
                      )}
                      {["placed", "pending", "confirmed", "preparing"].includes(selectedOrder.status) && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder._id, "cancelled")
                            setShowOrderDetails(false)
                          }}
                          className="px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors dark:bg-gray-700 dark:text-white"
                        >
                          {t("cancelOrder")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Flex>
          </ScrollArea>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  )
}

export default RestaurantOrdersPage
